import { zodResolver } from '@hookform/resolvers/zod';
import { useLingui } from '@lingui/react/macro';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { useController, useForm, type Control } from 'react-hook-form';

import { FieldGrid } from '@/shared/form';
import { ApiError, type ErrorFeedback } from '@/shared/http';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { FormField } from '@/shared/ui/field';
import { CodeInput, type CodeInputStatus } from '@/shared/ui/code-input';
import { Input } from '@/shared/ui/input';

import {
  STEP_UP_DEFAULTS,
  isGrantConfirm,
  isInvalidMfa,
  isStepUpRequired,
  stepUpErrorCode,
  stepUpErrorMessage,
  stepUpProof,
  stepUpSchema,
  type StepUpConfirm,
  type StepUpGrantConfirm,
  type StepUpProof,
  type StepUpValues,
} from './step-up';
import { startStepUpChallenge, startStepUpSession, verifyStepUpChallenge } from './step-up.api';
import { useStepUpRuntime } from './step-up-runtime';

type Slot = ComponentType<{ readonly className?: string; readonly children: ReactNode }>;

export interface StepUpFormProps<R> {
  /**
   * A operação sensível: com a prova (`sessionId` + `code`) no query ou no corpo, como o endpoint do legado; ou, no
   * step-up novo (`grantConfirm`), com a autorização de uso único que vira `X-UY3-StepUp`.
   */
  readonly confirm: StepUpConfirm<R>;
  readonly onDone: (result: R) => void;
  readonly onBack: () => void;
  readonly submitLabel: string;
  readonly backLabel: string;
  /** Enquanto envia, quem hospeda não deixa fechar (Esc, fundo, arraste). */
  readonly onBusyChange?: (busy: boolean) => void;
  /** Erro da operação que o hospedeiro trata (campo do formulário da ação). `true` = tratado. */
  readonly onOperationError?: (error: ApiError) => boolean;
  /** O que vem antes dos campos (prévia da ação, aviso). */
  readonly intro?: ReactNode;
  readonly Body: Slot;
  readonly Footer: Slot;
  /** Botão de confirmar em destaque destrutivo (`danger-solid`). */
  readonly destructive?: boolean;
}

/** Espera da entrada do painel antes de focar a senha (`PASSWORD_FOCUS_DELAY_MS` do legado). */
const PASSWORD_FOCUS_DELAY_MS = 350;

type Outcome<R> = { readonly done: true; readonly result: R } | { readonly done: false };
const NOT_DONE = { done: false } as const;

/**
 * Senha + código TOTP e o envio do step-up (`ConfirmMfaForm.tsx` do legado): `StartAuth` com a senha,
 * depois a operação com `sessionId` e `code`; no step-up novo, `StepUp/Start` + `StepUp/Verify` e a operação
 * com o `X-UY3-StepUp`. Senha e código ficam só no estado deste formulário, que é zerado ao concluir e some ao
 * fechar; nada vai para URL, storage, log ou agente.
 */
export function StepUpForm<R>(props: StepUpFormProps<R>) {
  const { i18n, t } = useLingui();
  const runtime = useStepUpRuntime();
  const form = useForm<StepUpValues, unknown, StepUpValues>({
    resolver: zodResolver(stepUpSchema(i18n)),
    defaultValues: STEP_UP_DEFAULTS,
    mode: 'onTouched',
  });
  const [failure, setFailure] = useState<ErrorFeedback | undefined>(undefined);
  // Slots do código: `error` drena (INVALID_MFA), `success` lava e mostra o check antes de concluir.
  const [codeStatus, setCodeStatus] = useState<CodeInputStatus>('idle');
  const [busy, setBusyState] = useState(false);
  const running = useRef(false);
  const controller = useRef<AbortController | null>(null);
  const { onBusyChange } = props;

  // O check do sucesso é só visual: se o hospedeiro sai antes (navegação), a pausa termina na hora e o
  // `onDone` roda ainda na desmontagem, sem cair na tela (ou no teste) seguinte.
  const finishPause = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      controller.current?.abort();
      finishPause.current?.();
    },
    [],
  );

  // O campo volta a ter foco depois do erro, quando o fieldset deixa de estar desabilitado.
  const [refocus, setRefocus] = useState<{ readonly count: number; readonly field: keyof StepUpValues }>({ count: 0, field: 'code' });
  useEffect(() => {
    if (refocus.count > 0 && !busy) form.setFocus(refocus.field);
  }, [refocus, busy, form]);
  const focus = (field: keyof StepUpValues) => setRefocus((previous) => ({ count: previous.count + 1, field }));

  // Step-up novo: a senha ganha o foco ao abrir (`ConfirmMfaForm.tsx`, PR 11323 do legado), depois da entrada do painel,
  // que ainda move o foco para o container.
  const grantMode = isGrantConfirm(props.confirm);
  useEffect(() => {
    if (!grantMode) return undefined;
    const timer = setTimeout(() => form.setFocus('password'), PASSWORD_FOCUS_DELAY_MS);
    return () => clearTimeout(timer);
  }, [grantMode, form]);

  const setBusy = (next: boolean) => {
    running.current = next;
    setBusyState(next);
    onBusyChange?.(next);
  };

  // O código errado sai do formulário já, não ao fim da drenagem dos slots: senão um envio rápido
  // repetiria o código recusado e o que se digita durante a animação se perderia.
  const rejectCode = (message: string) => {
    form.setValue('code', '');
    setCodeStatus('error');
    form.setError('code', { type: 'server', message });
    focus('code');
  };

  const stepUpFailure = (error: ApiError, message: string, description = ''): ErrorFeedback => ({
    tone: 'error',
    title: message,
    description,
    fieldErrors: [],
    ...(error.code === 'UNKNOWN' ? {} : { code: error.code }),
    ...(error.correlationId ? { correlationId: error.correlationId } : {}),
  });

  const operationFailed = (apiError: ApiError) => {
    if (props.onOperationError?.(apiError)) return;
    setFailure(apiError.feedback());
  };

  const runSession = async (confirm: (proof: StepUpProof) => Promise<R>, values: StepUpValues, signal: AbortSignal): Promise<Outcome<R>> => {
    let sessionId: string;
    try {
      sessionId = await startStepUpSession(values.password, { username: runtime.username(), signal });
    } catch (error) {
      const apiError = ApiError.from(error);
      if (!apiError.isCanceled) setFailure(apiError.feedback());
      return NOT_DONE;
    }
    try {
      return { done: true, result: await confirm(stepUpProof(sessionId, values.code)) };
    } catch (error) {
      const apiError = ApiError.from(error);
      if (apiError.isCanceled) return NOT_DONE;
      if (isInvalidMfa(apiError)) rejectCode(apiError.feedback().description);
      else operationFailed(apiError);
      return NOT_DONE;
    }
  };

  // Step-up novo (`stepUpContext.tsx` do legado): cada envio abre um desafio novo, porque o do Cognito não sobrevive
  // a um código errado; a autorização é de uso único e é consumida mesmo quando a operação falha.
  const runGrant = async (confirm: StepUpGrantConfirm<R>, values: StepUpValues, signal: AbortSignal): Promise<Outcome<R>> => {
    const challengeFailed = (error: unknown, onCode: boolean) => {
      const apiError = ApiError.from(error);
      if (apiError.isCanceled) return NOT_DONE;
      const message = stepUpErrorMessage(apiError, i18n, { challenge: true });
      const code = stepUpErrorCode(apiError);
      if (onCode && message && (code === 'STEP_UP_INVALID_CREDENTIALS' || code === 'INVALID_FORM')) rejectCode(message);
      else setFailure(message ? stepUpFailure(apiError, message) : apiError.feedback());
      return NOT_DONE;
    };
    let challengeId: string;
    try {
      challengeId = (await startStepUpChallenge(values.password, confirm.grant, signal)).challengeId;
    } catch (error) {
      return challengeFailed(error, false);
    }
    let grant;
    try {
      grant = await verifyStepUpChallenge(challengeId, values.code, signal);
    } catch (error) {
      return challengeFailed(error, true);
    }
    try {
      return { done: true, result: await confirm.run(grant) };
    } catch (error) {
      const apiError = ApiError.from(error);
      if (apiError.isCanceled) return NOT_DONE;
      if (isStepUpRequired(apiError)) {
        // Autorização vencida ou já usada: a operação não rodou. Senha e código de novo, no mesmo painel.
        form.reset(STEP_UP_DEFAULTS);
        setCodeStatus('idle');
        setFailure(
          stepUpFailure(apiError, stepUpErrorMessage(apiError, i18n) ?? '', t`A autorização venceu ou já foi usada e nada foi gravado. Informe a senha e o código de novo.`),
        );
        focus('password');
        return NOT_DONE;
      }
      // A autorização já foi consumida: a próxima tentativa abre outro desafio, com outro código.
      form.setValue('code', '');
      const message = stepUpErrorMessage(apiError, i18n);
      if (message) setFailure(stepUpFailure(apiError, message));
      else operationFailed(apiError);
      return NOT_DONE;
    }
  };

  const send = async (values: StepUpValues) => {
    if (running.current) return;
    setBusy(true);
    setFailure(undefined);
    const abort = new AbortController();
    controller.current = abort;
    try {
      const { confirm } = props;
      const outcome = isGrantConfirm(confirm) ? await runGrant(confirm, values, abort.signal) : await runSession(confirm, values, abort.signal);
      if (!outcome.done) return;
      setCodeStatus('success');
      await new Promise<void>((resolve) => {
        const timer = setTimeout(() => finishPause.current?.(), globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches ? 150 : 650);
        finishPause.current = () => {
          clearTimeout(timer);
          finishPause.current = null;
          resolve();
        };
      });
      form.reset(STEP_UP_DEFAULTS);
      setCodeStatus('idle');
      props.onDone(outcome.result);
    } finally {
      controller.current = null;
      setBusy(false);
    }
  };

  const { Body, Footer } = props;
  const code = failure?.code;
  const correlation = failure?.correlationId;

  return (
    <form
      noValidate
      data-slot="step-up-form"
      className="flex min-h-0 flex-1 flex-col gap-4"
      onSubmit={(event) => {
        // O painel mora num portal; o submit não pode subir até um formulário da tela.
        event.stopPropagation();
        void form.handleSubmit(send)(event);
      }}
    >
      <Body className="flex flex-col gap-4">
        {props.intro}
        {failure ? (
          <Alert tone={failure.tone === 'warning' ? 'warn' : 'err'} title={failure.title}>
            {failure.description ? <p>{failure.description}</p> : null}
            {code || correlation ? <p className="mt-1 font-mono text-xs">{[code, correlation].filter(Boolean).join(' · ')}</p> : null}
          </Alert>
        ) : null}
        <fieldset disabled={busy} className="min-w-0">
          <FieldGrid>
            <p className="text-sm text-muted-foreground">{t`Digite a sua senha`}</p>
            <PasswordField control={form.control} label={t`Senha`} />
            <CodeField
              control={form.control}
              label={t`Confirmação de código MFA`}
              hint={t`Informe o código que está presente no seu aplicativo MFA`}
              status={codeStatus}
              onTyping={() => {
                if (codeStatus === 'error') setCodeStatus('idle');
              }}
            />
          </FieldGrid>
        </fieldset>
      </Body>
      <Footer>
        <Button type="button" variant="secondary" disabled={busy} onClick={props.onBack}>
          {props.backLabel}
        </Button>
        <Button type="submit" variant={props.destructive ? 'danger-solid' : 'primary'} loading={busy}>
          {props.submitLabel}
        </Button>
      </Footer>
    </form>
  );
}

function PasswordField({ control, label }: { readonly control: Control<StepUpValues>; readonly label: string }) {
  const { t } = useLingui();
  const { field, fieldState } = useController({ name: 'password', control });
  const [visible, setVisible] = useState(false);
  return (
    <FormField name="password" label={label} required error={fieldState.error?.message}>
      {(controlProps) => (
        <div className="relative">
          <Input
            {...controlProps}
            ref={field.ref}
            type={visible ? 'text' : 'password'}
            autoComplete="current-password"
            autoCapitalize="off"
            spellCheck={false}
            value={field.value}
            onChange={(event) => field.onChange(event.target.value)}
            onBlur={field.onBlur}
            className="pr-11"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-pressed={visible}
            aria-label={visible ? t`Ocultar senha` : t`Mostrar senha`}
            className="absolute top-1/2 right-1 size-8 -translate-y-1/2 text-muted-foreground"
            onClick={() => setVisible((previous) => !previous)}
          >
            {visible ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
          </Button>
        </div>
      )}
    </FormField>
  );
}

function CodeField({
  control,
  label,
  hint,
  status,
  onTyping,
}: {
  readonly control: Control<StepUpValues>;
  readonly label: string;
  readonly hint: string;
  readonly status: CodeInputStatus;
  readonly onTyping: () => void;
}) {
  const { field, fieldState } = useController({ name: 'code', control });
  return (
    <FormField name="code" label={label} required hint={hint} error={fieldState.error?.message}>
      {(controlProps) => (
        <CodeInput
          id={controlProps.id}
          name={field.name}
          required
          aria-labelledby={controlProps['aria-labelledby']}
          {...(controlProps['aria-describedby'] ? { 'aria-describedby': controlProps['aria-describedby'] } : {})}
          invalid={Boolean(controlProps['aria-invalid'])}
          ref={field.ref}
          value={field.value}
          onChange={(code) => {
            field.onChange(code);
            if (code) onTyping();
          }}
          onBlur={field.onBlur}
          status={status}
          className="max-w-[22rem]"
        />
      )}
    </FormField>
  );
}
